import { analyzeCategory } from '../../../utils/categoryClassifier';
import {
  CARE_MEDICATION_MULTI_DOMAIN_ACTION_NAME_PATTERN,
  CARE_MEDICATION_MULTI_DOMAIN_NAME_PATTERN,
} from '../../../shared/careMedicationPatterns';
import { normalizeImportCategory } from './importLogsParsingClassification';
import { buildSplitEntryTitle } from './importLogsCategoryResolutionTitles';
import {
  INLINE_CAUSAL_SPLIT_PATTERNS,
  countWords,
  ensureSentence,
  getLeadingRelationshipKind,
  hasExplicitRelationshipSignal,
  hasMultiDomainSentenceBoundary,
  splitSentenceParts,
  stripLeadingRelationshipConnector,
  toSentenceCase,
} from './importLogsRelationshipConnectors';

const DISTINCT_LOG_CATEGORY_SET = new Set(['behavior', 'medication', 'food', 'sleep', 'activity', 'mood', 'milestone', 'health']);
const ENUMERATION_SPLIT_PATTERN = /,\s+|\s+and\s+/i;

const getExplicitSplitClauseCategory = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return null;
  }

  if (/\b(?:rough night|fell asleep|falling asleep|slept|sleeping|bedtime|woke up|woke)\b/i.test(comparable)) {
    return 'sleep';
  }
  if (/\b(?:behavior(?:al)? incident|school incident|incident at school|school called about an incident|teacher reported .*incident|attempted hitting|hit(?:ting)?|slap(?:ped|ping)?|push(?:ed|ing)?|kick(?:ed|ing)?|yell(?:ed|ing)?|cry(?:ing|ied)?)\b/i.test(comparable)) {
    return 'behavior';
  }
  if (CARE_MEDICATION_MULTI_DOMAIN_ACTION_NAME_PATTERN.test(comparable) || CARE_MEDICATION_MULTI_DOMAIN_NAME_PATTERN.test(comparable)) {
    return 'medication';
  }
  if (/\b(?:appetite|hungry|ate|eating|lunch|breakfast|dinner|snack|food)\b/i.test(comparable)) {
    return 'food';
  }
  if (/\b(?:participated|joined|activity|project|class|recess|therapy|music)\b/i.test(comparable)) {
    return 'activity';
  }
  if (/\b(?:happy|sad|anxious|tired|worried|excited|calm)\b/i.test(comparable)) {
    return 'mood';
  }

  return null;
};

const isDistinctDomainClause = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return null;
  }

  const explicitCategory = getExplicitSplitClauseCategory(comparable);
  if (explicitCategory && DISTINCT_LOG_CATEGORY_SET.has(explicitCategory)) {
    return {
      category: explicitCategory,
      confidence: 'high',
    };
  }

  const analysis = analyzeCategory(comparable, { allowUnknown: true });
  const category = normalizeImportCategory(analysis?.category || 'log');
  if (!DISTINCT_LOG_CATEGORY_SET.has(category)) {
    return null;
  }

  return {
    category,
    confidence: analysis?.confidence || 'low',
  };
};

const trySplitInlineRelationshipClause = (sentence = '') => {
  const comparable = String(sentence || '').trim();
  if (!comparable) {
    return null;
  }

  for (const pattern of INLINE_CAUSAL_SPLIT_PATTERNS) {
    const match = pattern.exec(comparable);
    if (!match || typeof match.index !== 'number') {
      continue;
    }

    const left = comparable.slice(0, match.index).replace(/[,\s]+$/u, '').trim();
    const right = comparable.slice(match.index).replace(/^[,\s]+/u, '').trim();
    const leftInfo = isDistinctDomainClause(left);
    const rightInfo = isDistinctDomainClause(stripLeadingRelationshipConnector(right));

    if (!leftInfo || !rightInfo || leftInfo.category === rightInfo.category) {
      continue;
    }

    return [
      { originalClause: left, relationshipKind: 'none', ...leftInfo },
      { originalClause: right, relationshipKind: 'causal', ...rightInfo },
    ];
  }

  return null;
};

const trySplitEnumeratedDomainClauses = (sentence = '') => {
  const comparable = String(sentence || '').trim();
  if (!comparable || !ENUMERATION_SPLIT_PATTERN.test(comparable)) {
    return null;
  }

  const rawParts = comparable
    .split(/,\s+|\s+and\s+/i)
    .map((part) => String(part || '').trim())
    .filter(Boolean);

  if (rawParts.length < 2 || rawParts.length > 4) {
    return null;
  }

  const analyzedParts = rawParts.map((part) => {
    const info = isDistinctDomainClause(part);
    return info ? { originalClause: part, relationshipKind: 'none', ...info } : null;
  });

  if (analyzedParts.some((part) => !part)) {
    return null;
  }

  const categories = new Set(analyzedParts.map((part) => part.category));
  if (categories.size < 2) {
    return null;
  }

  return analyzedParts;
};

const buildCandidateMultiDomainClauses = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable || !hasMultiDomainSentenceBoundary(comparable)) {
    return [];
  }

  const sentenceParts = splitSentenceParts(comparable);
  const clauses = [];

  sentenceParts.forEach((sentence) => {
    const causalParts = trySplitInlineRelationshipClause(sentence);
    if (causalParts?.length) {
      clauses.push(...causalParts);
      return;
    }

    const enumeratedParts = trySplitEnumeratedDomainClauses(sentence);
    if (enumeratedParts?.length) {
      clauses.push(...enumeratedParts);
      return;
    }

    const stripped = stripLeadingRelationshipConnector(sentence);
    const info = isDistinctDomainClause(stripped);
    if (info) {
      clauses.push({
        originalClause: sentence,
        relationshipKind: getLeadingRelationshipKind(sentence),
        ...info,
      });
    }
  });

  return clauses;
};

const formatPredecessorReference = (row = {}) => {
  const category = normalizeImportCategory(row?.category || 'log');
  const note = String(row?.note || row?.body || row?.text || '').trim();
  if (category === 'behavior') {
    return /\bincident\b/i.test(note) ? 'the behavior incident' : 'the behavior event';
  }
  if (category === 'sleep' && /\brough night\b/i.test(note)) {
    return 'the rough night';
  }

  const title = String(row?.title || '').trim();
  if (!title) {
    return 'the earlier event';
  }

  const normalized = title.toLowerCase();
  if (/^(?:the|this|that|an?|my|our)\b/.test(normalized)) {
    return normalized;
  }

  return `the ${normalized}`;
};

const buildCarriedNote = ({
  text = '',
  relationshipKind = 'none',
  previousRow = null,
}) => {
  const stripped = stripLeadingRelationshipConnector(text);
  const sentence = ensureSentence(toSentenceCase(stripped));
  if (!sentence) {
    return '';
  }

  if (relationshipKind === 'temporal') {
    const base = sentence.replace(/[.!?]$/u, '');
    return `${base} later that day.`;
  }

  if (relationshipKind === 'causal' && previousRow) {
    const base = sentence.replace(/[.!?]$/u, '');
    return `${base} in response to ${formatPredecessorReference(previousRow)}.`;
  }

  return sentence;
};

export const shouldAttemptMultiDomainSplit = (rows = [], helpers = {}) => {
  const {
    getComparableText = () => '',
    getSourceBlockText = () => '',
    pickRepresentativeRow = () => null,
    textContains = () => false,
  } = helpers;

  if (!Array.isArray(rows) || rows.length === 0 || rows.length > 4) {
    return false;
  }

  const representative = pickRepresentativeRow(rows);
  const representativeText = getSourceBlockText(representative) || getComparableText(representative);
  if (!representativeText || representativeText.length > 220) {
    return false;
  }

  if (!hasMultiDomainSentenceBoundary(representativeText)) {
    return false;
  }

  if (!rows.every((row) => {
    const rowText = getComparableText(row);
    return !rowText || textContains(representativeText, rowText) || textContains(rowText, representativeText);
  })) {
    return false;
  }

  const analyzedClauses = buildCandidateMultiDomainClauses(representativeText);
  const distinctCategories = new Set(analyzedClauses.map((clause) => clause.category));
  if (!(analyzedClauses.length > 1 && distinctCategories.size > 1)) {
    return false;
  }

  if (hasExplicitRelationshipSignal(representativeText)) {
    return true;
  }

  return analyzedClauses.length >= 2
    && analyzedClauses.every((clause) => countWords(clause.comparableClause || clause.originalClause) <= 8)
    && representativeText.length <= 120;
};

export const buildSplitRowsFromSourceBlock = (rows = [], helpers = {}) => {
  const {
    getComparableText = () => '',
    getSourceBlockText = () => '',
    pickRepresentativeRow = () => null,
    textContains = () => false,
  } = helpers;

  const representative = pickRepresentativeRow(rows);
  const sourceBlockText = getSourceBlockText(representative) || getComparableText(representative);
  if (!representative || !sourceBlockText) {
    return rows;
  }

  const analyzedClauses = buildCandidateMultiDomainClauses(sourceBlockText).map((clause, index) => ({
    ...clause,
    index,
    comparableClause: stripLeadingRelationshipConnector(clause.originalClause),
  }));

  const distinctCategories = new Set(analyzedClauses.map((clause) => clause.category));
  if (analyzedClauses.length <= 1 || distinctCategories.size <= 1) {
    return rows;
  }

  const usedRows = new Set();
  const rebuiltRows = [];

  analyzedClauses.forEach((clause, index) => {
    const matchingRow = rows.find((row) => {
      if (usedRows.has(row)) {
        return false;
      }
      const rowCategory = normalizeImportCategory(row?.category || 'log');
      const rowText = getComparableText(row);
      return rowCategory === clause.category && (
        textContains(rowText, clause.comparableClause)
        || textContains(clause.comparableClause, rowText)
      );
    }) || representative;

    if (matchingRow !== representative) {
      usedRows.add(matchingRow);
    }

    const previousResolvedRow = rebuiltRows[index - 1] || null;
    const noteText = buildCarriedNote({
      text: clause.originalClause,
      relationshipKind: clause.relationshipKind,
      previousRow: previousResolvedRow,
    }) || ensureSentence(toSentenceCase(clause.comparableClause));

    const resolvedTitle = String(
      matchingRow?.title
      && !textContains(sourceBlockText, String(matchingRow.title || '').trim())
        ? matchingRow.title
        : buildSplitEntryTitle(clause.category, noteText)
    ).trim();

    rebuiltRows.push({
      ...matchingRow,
      category: clause.category,
      categoryHint: clause.category,
      title: resolvedTitle,
      titlePrefix: resolvedTitle,
      titleHint: resolvedTitle,
      body: noteText,
      note: noteText,
      text: noteText,
      content: noteText,
      details: noteText,
      description: noteText,
      summary: noteText,
      value: noteText,
      sourceText: sourceBlockText,
      originalText: sourceBlockText,
      rawText: sourceBlockText,
      rawSourceText: sourceBlockText,
      metadata: {
        ...(matchingRow.metadata || {}),
        categoryHint: clause.category,
        titleHint: resolvedTitle,
        multiDomainResolution: {
          applied: true,
          clauseIndex: clause.index,
          relationshipKind: clause.relationshipKind,
          sourceCategorySet: Array.from(distinctCategories),
        },
      },
    });
  });

  return rebuiltRows;
};
